/**
 * Cadence + period option model for the entity performance report's date
 * selectors.
 *
 * The report exposes two dropdowns:
 *
 *  - **Cadence** — Daily / Weekly / Monthly (weekly is the default).
 *  - **Period**  — the concrete window within that cadence, listed newest-first
 *                  (e.g. "14 Sep 2026", "7 Sep 2026", … for weekly).
 *
 * This module is the pure, section-agnostic core behind those dropdowns:
 * {@link buildPeriodOptions} enumerates the period options for a cadence and
 * resolves each to a concrete `{ start, stop, frequency }` window that feeds
 * `useReport` / `Report`. It performs no I/O and holds no React state, so it is
 * cheaply unit-testable.
 *
 * Bucketing (graph granularity) is fixed per cadence, not user-selectable:
 *
 *  | Cadence | Window span      | Limit | Graph buckets (`frequency`) |
 *  |---------|------------------|-------|-----------------------------|
 *  | Daily   | one calendar day | 30    | `hourly`                    |
 *  | Weekly  | Mon–Sun cal. week| 24    | `daily`                     |
 *  | Monthly | one calendar mo. | 24    | `daily`                     |
 *
 * All bounds are returned as ISO 8601 strings snapped to day/week/month edges,
 * mirroring `useReport`'s window handling (the backend's `parse_iso_timestamptz`
 * accepts them directly). Dates are computed in the browser's local time zone
 * using `date-fns` only — deliberately no `timezonecomplete`, to stay in step
 * with `useReport`.
 *
 * The `lowerBound` clamp keeps any period whose window *overlaps* the bound, not
 * only those starting on/after it — so a calendar month/week the entity started
 * partway through is still offered (see {@link endsBeforeLowerBound}).
 */
import { format, isBefore, startOfDay } from "date-fns"
import { IANATimeZone } from "@hydrologiq/common/helpers/date"
import type { Frequency } from "./frequency"
import {
  zonedEndOfDay,
  zonedEndOfMonth,
  zonedEndOfWeek,
  zonedStartOfDay,
  zonedStartOfMonth,
  zonedStartOfWeek,
  zonedSubDays,
  zonedSubMonths,
  zonedSubWeeks,
} from "./zonedPeriod"

/**
 * Format an instant's calendar date in the report's timezone.
 *
 * Labels/ids must reflect the day *in the report zone* (the same zone the
 * window is snapped to), not the browser's local day — otherwise a UTC browser
 * reporting on Europe/London could label a window with the previous day. Built
 * from the zone's own Y/M/D parts and fed through `date-fns` `format` for the
 * existing label shapes.
 */
function formatInZone(instant: Date, pattern: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(instant)
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0")
  // A local (zoneless) Date carrying the report-zone's calendar day, so
  // `date-fns` `format` prints that day regardless of the browser zone.
  const local = new Date(get("year"), get("month") - 1, get("day"))
  return format(local, pattern)
}

/** The three report cadences the cadence dropdown offers. */
export type Cadence = "daily" | "weekly" | "monthly"

/** The default cadence a report opens on (per product spec: Weekly). */
export const DEFAULT_CADENCE: Cadence = "weekly"

export interface CadenceOption {
  value: Cadence
  label: string
}

/** Cadence dropdown items, in display order. */
export const CADENCE_OPTIONS: CadenceOption[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
]

/**
 * A single period-dropdown option: a human label plus the concrete window it
 * resolves to (the `start` / `stop` / `frequency` handed to `useReport`).
 */
export interface PeriodOption {
  /** Stable key for React lists + `<option value>` (ISO day of the anchor). */
  id: string
  /** Human label shown in the dropdown (e.g. "14 Sep 2026" or "Sep 2026"). */
  label: string
  /** ISO 8601 window start (inclusive), snapped to a day/week/month edge. */
  start: string
  /** ISO 8601 window end, snapped to end-of-day/week/month. */
  stop: string
  /** Graph bucket granularity for this cadence (hourly for daily, else daily). */
  frequency: Frequency
}

/** Per-cadence enumeration config: how many options and which graph buckets. */
interface CadenceConfig {
  /** Maximum number of period options to emit. */
  limit: number
  /** Graph bucket granularity sent as `frequency`. */
  frequency: Frequency
}

const CADENCE_CONFIG: Record<Cadence, CadenceConfig> = {
  daily: { limit: 30, frequency: "hourly" },
  weekly: { limit: 24, frequency: "daily" },
  monthly: { limit: 24, frequency: "daily" },
}

/** Day-label format used for daily and weekly options (e.g. "14 Sep 2026"). */
const DAY_LABEL = "d MMM yyyy"
/** Month-label format used for monthly options (e.g. "Sep 2026"). */
const MONTH_LABEL = "MMM yyyy"

/**
 * True when a period's window ends *before* the lower bound, i.e. the entire
 * window predates when the entity existed and so should be excluded.
 *
 * The check is on the window's **end**, not its start: a period whose window
 * merely *overlaps* the bound must be kept. This matters for calendar-aligned
 * cadences — a monthly window starts on the 1st, so if the entity started
 * mid-month (e.g. 15 Aug) the August option's start (1 Aug) precedes the bound
 * yet August is still a valid, overlapping reporting month and must be offered.
 * Comparing on the window end (31 Aug ≥ 15 Aug) keeps it. The bound is taken at
 * start-of-day so any overlap on the bound's own day counts.
 */
function endsBeforeLowerBound(windowStop: Date, lowerBound?: Date): boolean {
  if (!lowerBound) return false
  return isBefore(windowStop, startOfDay(lowerBound))
}

/**
 * Build the daily period options: one per calendar day, newest first, each a
 * single full day with hourly graph buckets. Capped at the daily limit (30) and
 * never running earlier than `lowerBound`'s day.
 */
function buildDailyOptions(
  anchorEnd: Date,
  lowerBound: Date | undefined,
  limit: number,
  frequency: Frequency,
  timeZone: string,
): PeriodOption[] {
  const options: PeriodOption[] = []
  for (let i = 0; i < limit; i++) {
    const day = zonedSubDays(anchorEnd, i, timeZone)
    const start = zonedStartOfDay(day, timeZone)
    const stop = zonedEndOfDay(day, timeZone)
    if (endsBeforeLowerBound(stop, lowerBound)) break
    options.push({
      id: formatInZone(start, "yyyy-MM-dd", timeZone),
      label: formatInZone(start, DAY_LABEL, timeZone),
      start: start.toISOString(),
      stop: stop.toISOString(),
      frequency,
    })
  }
  return options
}

/** Day `startOfWeek`/`endOfWeek` treat as the first day of the week (Monday). */
const WEEK_STARTS_ON = 1 as const

/**
 * Build the weekly period options: one per **calendar week** (Monday–Sunday),
 * newest first, each spanning the whole week with daily graph buckets. Labelled
 * and identified by the week-start (Monday) date — e.g. the week containing
 * 14 Sep 2026 (a Monday) reads "8 Sep 2026" for the Mon 8 – Sun 14 week.
 * Capped at the weekly limit (24) and clamped to `lowerBound`.
 */
function buildWeeklyOptions(
  anchorEnd: Date,
  lowerBound: Date | undefined,
  limit: number,
  frequency: Frequency,
  timeZone: string,
): PeriodOption[] {
  const options: PeriodOption[] = []
  for (let i = 0; i < limit; i++) {
    const dayInWeek = zonedSubWeeks(anchorEnd, i, timeZone, WEEK_STARTS_ON)
    const start = zonedStartOfWeek(dayInWeek, timeZone, WEEK_STARTS_ON)
    const stop = zonedEndOfWeek(dayInWeek, timeZone, WEEK_STARTS_ON)
    if (endsBeforeLowerBound(stop, lowerBound)) break
    options.push({
      id: formatInZone(start, "yyyy-MM-dd", timeZone),
      label: formatInZone(start, DAY_LABEL, timeZone),
      start: start.toISOString(),
      stop: stop.toISOString(),
      frequency,
    })
  }
  return options
}

/**
 * Build the monthly period options: one per calendar month, newest first, each
 * spanning the whole month with daily graph buckets. Capped at the monthly limit
 * (24) and clamped to `lowerBound`.
 */
function buildMonthlyOptions(
  anchorEnd: Date,
  lowerBound: Date | undefined,
  limit: number,
  frequency: Frequency,
  timeZone: string,
): PeriodOption[] {
  const options: PeriodOption[] = []
  for (let i = 0; i < limit; i++) {
    const monthDay = zonedSubMonths(anchorEnd, i, timeZone)
    const start = zonedStartOfMonth(monthDay, timeZone)
    const stop = zonedEndOfMonth(monthDay, timeZone)
    if (endsBeforeLowerBound(stop, lowerBound)) break
    options.push({
      id: formatInZone(start, "yyyy-MM", timeZone),
      label: formatInZone(start, MONTH_LABEL, timeZone),
      start: start.toISOString(),
      stop: stop.toISOString(),
      frequency,
    })
  }
  return options
}

/**
 * Enumerate the period-dropdown options for a cadence, resolved to concrete
 * `{ start, stop, frequency }` windows.
 *
 * Options are ordered **newest first** (index 0 is the most recent period — the
 * default selection). Enumeration walks backwards from `anchorEnd` (defaults to
 * now), stops at `lowerBound` (the entity's start, if known), and is capped at
 * the cadence's limit (daily 30, weekly 24, monthly 24). There is always at
 * least one option (the period containing `anchorEnd`), even when `lowerBound`
 * is after `anchorEnd`.
 *
 * @param cadence    The selected cadence (daily / weekly / monthly).
 * @param anchorEnd  The most-recent instant the report should reach (e.g. the
 *                   asset's assignment end, or now). Defaults to now.
 * @param lowerBound The earliest instant options may reach (e.g. the entity's
 *                   assignment/system start). When omitted, only the limit caps
 *                   the list.
 */
export function buildPeriodOptions(
  cadence: Cadence,
  anchorEnd: Date = new Date(),
  lowerBound?: Date,
  timeZone: string = IANATimeZone(),
): PeriodOption[] {
  const { limit, frequency } = CADENCE_CONFIG[cadence]
  switch (cadence) {
    case "daily":
      return buildDailyOptions(anchorEnd, lowerBound, limit, frequency, timeZone)
    case "weekly":
      return buildWeeklyOptions(anchorEnd, lowerBound, limit, frequency, timeZone)
    case "monthly":
      return buildMonthlyOptions(anchorEnd, lowerBound, limit, frequency, timeZone)
  }
}
